/**
 * Sujets de note, leurs libellés et leurs couleurs.
 *
 * Source unique pour l'écran : le formulaire de saisie, le filtre de l'archive, la pilule de
 * la liste, les infobulles des graphiques et les pastilles du tableau consolidé lisent tous
 * cette table. Les clés sont celles de l'enum `NoteTopic` de Prisma — les recopier ailleurs
 * les ferait diverger du schéma au premier ajout.
 *
 * **Les couleurs sont choisies, pas prises au hasard.** Chaque teinte de texte dépasse 4,5:1
 * de contraste sur blanc, et l'émeraude de l'opération commerciale évite qu'on la confonde
 * avec l'ambre `#f59e0b` que gardent les notes sans sujet. Le libellé accompagne toujours la
 * pastille, sauf sur les points de huit pixels des graphiques, où l'infobulle le donne au
 * survol : la couleur n'y porte donc jamais seule une information qu'on ne pourrait pas lire.
 */

/** Couleur de repli d'une note sans sujet — le défaut historique du champ `color`. */
export const COULEUR_SANS_SUJET = '#f59e0b';

/** Valeur de filtre demandant les notes qu'aucun sujet ne couvre. Doit valoir celle du serveur. */
export const SANS_SUJET = '__sans__';

/**
 * L'ordre est celui des listes déroulantes, et il est délibéré : les sujets qui relèvent du
 * pilotage viennent d'abord, « Bug » ensuite, « Autre » en dernier — un fourre-tout se lit
 * en fin de liste, pas au milieu.
 *
 * Il ne suit pas celui de l'enum Prisma, dont l'ordre n'a aucune portée visible.
 */
export const NOTE_TOPICS = [
  { value: 'EVENEMENT', label: 'Événement', couleur: '#2563eb', fond: '#eff6ff' },
  { value: 'OPERATION_COMMERCIALE', label: 'Opération commerciale', couleur: '#047857', fond: '#ecfdf5' },
  { value: 'CONCURRENCE', label: 'Concurrence', couleur: '#7c3aed', fond: '#f5f3ff' },
  { value: 'BUG', label: 'Bug', couleur: '#dc2626', fond: '#fef2f2' },
  { value: 'AUTRE', label: 'Autre', couleur: '#4b5563', fond: '#f3f4f6' }
];

const PAR_CLE = new Map(NOTE_TOPICS.map(t => [t.value, t]));

/** Le sujet d'une note, ou `null` si elle n'en porte pas — ou en porte un que le front ignore. */
export const sujetDeNote = (cle) => (cle ? PAR_CLE.get(cle) || null : null);

/** Libellé d'un sujet. Une clé inconnue est rendue telle quelle plutôt que masquée. */
export const libelleSujet = (cle) => (cle ? (PAR_CLE.get(cle)?.label || cle) : null);

/**
 * Couleur sous laquelle une note se montre : celle de son sujet, sinon la sienne.
 *
 * Sert aux pastilles du tableau consolidé et aux marqueurs des graphiques de performance, où
 * il n'y a la place ni pour un libellé ni pour une légende. Une note sans sujet garde donc la
 * couleur qu'elle avait, et rien ne change pour l'existant.
 *
 * Accepte les deux formes de note qui circulent dans l'application : celle de l'API REST
 * (`topic` / `color`) et celle, francisée, du dashboard consolidé (`sujet` / `couleur`).
 */
export const couleurDeNote = (note) => (
  sujetDeNote(note?.topic || note?.sujet)?.couleur
  || note?.color
  || note?.couleur
  || COULEUR_SANS_SUJET
);
